import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type CurvePoint,
  type GetRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGetData,
  type GetRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGetError,
  type RecommendationMetric,
  type RecommendationsResponse,
  getRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGet,
} from '../../generated';

/**
 * Frontend metric types (camelCase) matching @vizit/molecules types
 * These are local to the API package to avoid circular dependencies
 */
interface AreaMetric {
  key: string;
  type: 'area';
  impactDelta: number;
  scoreRange: { min: number; max: number };
  curve: CurvePoint[];
  breakpoints: [number, number];
  currentValue: number;
}

interface BarMetric {
  key: string;
  type: 'bar';
  impactDelta: number;
  range: { min: number; max: number };
  optimalBand: number[];
  currentValue: number;
}

interface BooleanMetric {
  key: string;
  type: 'boolean';
  impactDelta: number;
  currentValue: boolean;
}

type Metric = AreaMetric | BarMetric | BooleanMetric;

export interface GetRecommendationsResponse {
  metrics: Metric[];
}

/**
 * Type for 202 Accepted response (processing in progress)
 */
export interface GetRecommendationsAcceptedResponse {
  detail: string;
}

/**
 * Union type representing all possible responses from the recommendations endpoint
 * - GetRecommendationsResponse: 200 response with metrics
 * - GetRecommendationsAcceptedResponse: 202 response indicating processing in progress
 */
export type GetRecommendationsData = GetRecommendationsResponse | GetRecommendationsAcceptedResponse;

export type GetRecommendationsQueryVariables =
  GetRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGetData['path'];

export type GetRecommendationsQuery = GetRecommendationsData;

/**
 * Transforms API RecommendationMetric (snake_case) to frontend Metric (camelCase)
 */
const transformMetric = (apiMetric: RecommendationMetric): Metric => {
  const base = {
    key: apiMetric.key,
    type: apiMetric.type as 'area' | 'bar' | 'boolean',
    impactDelta: apiMetric.impact_delta,
  };

  if (apiMetric.type === 'area') {
    return {
      ...base,
      type: 'area',
      scoreRange: apiMetric.score_range
        ? { min: apiMetric.score_range.min, max: apiMetric.score_range.max }
        : { min: 0, max: 100 },
      curve: apiMetric.curve || [],
      breakpoints: [0, 0] as [number, number],
      currentValue: typeof apiMetric.current_value === 'number' ? Math.round(apiMetric.current_value) : 0,
    };
  }

  if (apiMetric.type === 'bar') {
    return {
      ...base,
      type: 'bar',
      range: apiMetric.range ? { min: apiMetric.range.min, max: apiMetric.range.max } : { min: 0, max: 100 },
      optimalBand: apiMetric.optimal_band || [],
      currentValue: typeof apiMetric.current_value === 'number' ? apiMetric.current_value : 0,
    };
  }

  // boolean type
  return {
    ...base,
    type: 'boolean',
    currentValue: typeof apiMetric.current_value === 'boolean' ? apiMetric.current_value : false,
  };
};

export const useGetRecommendationsQuery = <
  TData = GetRecommendationsQuery,
  TError = GetRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGetError,
>(
  { portfolio_id, pdp_id }: GetRecommendationsQueryVariables,
  options?: Omit<UseQueryOptions<GetRecommendationsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetRecommendationsQuery, TError, TData>({
    queryKey: ['GetRecommendations', { portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });

      // Transform metrics response (200) to frontend format
      if (data && typeof data === 'object' && 'metrics' in data) {
        const response = data as RecommendationsResponse;
        return {
          metrics: response.metrics.map(transformMetric),
        };
      }

      return data as GetRecommendationsData;
    },
    ...options,
  });
};

useGetRecommendationsQuery.getKey = ({ portfolio_id, pdp_id }: GetRecommendationsQueryVariables) => [
  'GetRecommendations',
  { portfolio_id, pdp_id },
];

useGetRecommendationsQuery.fetcher = (
  { portfolio_id, pdp_id }: GetRecommendationsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getRecommendationsV1PortfoliosPortfolioIdPdpsPdpIdRecommendationsGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });

    // Transform metrics response (200) to frontend format
    if (data && typeof data === 'object' && 'metrics' in data) {
      const response = data as RecommendationsResponse;
      return {
        metrics: response.metrics.map(transformMetric),
      };
    }

    // Return other responses unchanged (e.g., 202 Accepted with { detail: "..." })
    // This preserves processing state information for polling logic
    return data as GetRecommendationsData;
  };
};

useGetRecommendationsQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/recommendations',
};
